/**
 * `/room` — Design Room entry point from the app home and the plan chooser.
 * Rooms live inside a workspace (`/workspace/:id/room`), so this page only picks the workspace:
 * one workspace → straight redirect; none → creates "Design Room" and redirects; several → picker.
 */
import { useEffect, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { LayoutGrid, Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { api } from '#/lib/api'
import { authClient } from '#/lib/auth-client'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/room')({
  head: () => ({ meta: [{ title: m.dr_pick_meta_title() }] }),
  component: RoomEntry,
})

interface WorkspaceRow {
  id: string
  name: string
  updatedAt: string
}

function RoomEntry() {
  const { data: session, isPending } = authClient.useSession()
  const navigate = useNavigate()
  useEffect(() => {
    if (!isPending && !session?.user) navigate({ to: '/sign-in', search: { redirect: '/room' } })
  }, [isPending, session, navigate])
  const signedIn = Boolean(session?.user)
  const { data, isLoading } = useQuery({ queryKey: ['workspaces', 'room-entry'], queryFn: () => api<{ workspaces: WorkspaceRow[] }>('/api/workspaces'), enabled: signedIn })
  const create = useMutation({
    mutationFn: (name: string) => api<{ workspace: WorkspaceRow }>('/api/workspaces', { method: 'POST', json: { name } }),
    onSuccess: (r) => void navigate({ to: '/workspace/$workspaceId/room', params: { workspaceId: r.workspace.id } }),
    onError: () => toast.error(m.common_error_generic()),
  })
  const [name, setName] = useState('')
  const workspaces = data?.workspaces ?? []

  // one workspace → go straight in; none → make one (a room needs a home, and the plan it hands off to lives there too)
  useEffect(() => {
    if (!data) return
    if (workspaces.length === 1) void navigate({ to: '/workspace/$workspaceId/room', params: { workspaceId: workspaces[0]!.id }, replace: true })
    else if (workspaces.length === 0 && !create.isPending && !create.isSuccess) create.mutate(m.dr_pick_default_ws())
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <AppHeader showStats={false} compact />
      <main className="flex flex-1 flex-col items-center overflow-y-auto px-6 py-10">
        <div className="w-full max-w-2xl space-y-6">
          <div className="text-center">
            <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <LayoutGrid className="size-6" />
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold sm:text-3xl">{m.dr_pick_title()}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{m.dr_pick_lead()}</p>
          </div>
          {!signedIn || isLoading || workspaces.length <= 1 ? (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> {m.dr_pick_loading()}
            </p>
          ) : (
            <>
              <ul className="grid gap-3 sm:grid-cols-2">
                {workspaces.map((w) => (
                  <li key={w.id}>
                    <Link to="/workspace/$workspaceId/room" params={{ workspaceId: w.id }} className="flex h-full flex-col gap-1 rounded-2xl border bg-card p-4 transition-colors hover:border-primary/60">
                      <span className="font-heading text-base font-semibold">{w.name}</span>
                      <span className="text-xs text-muted-foreground">{m.dr_pick_updated({ date: fmtDate(w.updatedAt) })}</span>
                      <span className="mt-2 text-xs font-medium text-primary">{m.dr_pick_open()}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <form
                className="flex flex-col gap-2 rounded-2xl border border-dashed p-4 sm:flex-row sm:items-center"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (name.trim()) create.mutate(name.trim())
                }}
              >
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.dr_pick_new_ph()} maxLength={120} className="sm:flex-1" />
                <Button type="submit" disabled={!name.trim() || create.isPending} className="gap-1.5">
                  {create.isPending ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />} {m.dr_pick_new()}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </>
  )
}
